import type {
  VisualPayload,
  SeasonOverview,
  CupSlot,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  TYPE_CHIP_CLASSES,
  LEAGUE_BADGE_CLASSES,
  fmtDate,
  parseCupRules,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * Focused render for one cup's rules.
 *
 * Backend wraps the matched cup in a single-element cup_schedule list
 * (see _cup_rules_card in visual_assembler.py).  This component:
 *   1. parses the free-text rules_summary into structured fields
 *      (CP cap, eligible types, banned Pokémon),
 *   2. surfaces those as chips / badges / list,
 *   3. always falls back to rendering the raw rules narrative so
 *      partially-parseable strings still show their information.
 */
export default function CupRulesCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ?? {}) as SeasonOverview;
  const cupScheduleSec = sections.find((s) => s.section === "cup_schedule");
  const cups = (cupScheduleSec?.items as CupSlot[] | undefined) ?? [];
  const cup = cups[0]; // backend emits exactly one when found

  if (!cup) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
        <SeasonHeaderStrip overview={overview} />
        <p className="text-sm text-white/40 py-2">
          Couldn't identify which cup you meant. Try asking with the cup name (e.g. "What's allowed in Sunshine Cup?").
        </p>
        <SeasonSourceLink sourceUrl={payload.source_url} />
      </CardShell>
    );
  }

  const parsed = parseCupRules(cup.rules_summary);
  const leagueColor = cup.league ? LEAGUE_BADGE_CLASSES[cup.league.toLowerCase()] : null;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
      <SeasonHeaderStrip overview={overview} />

      {/* Cup heading: large name + dates + league badge */}
      <div className="mb-4 pb-3 border-b border-white/[0.06]">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-base font-semibold text-white leading-snug">{cup.cup_name ?? "GBL Cup"}</p>
          {leagueColor && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${leagueColor} shrink-0 mt-1`}>
              {cup.league}
            </span>
          )}
        </div>
        <p className="text-xs text-white/55 tabular-nums">
          {fmtDate(cup.week_start)}{cup.week_end && <> – {fmtDate(cup.week_end)}</>}
        </p>
      </div>

      {/* Structured: CP cap pill */}
      {parsed.cp_cap !== null && (
        <div className="mb-3 flex items-center gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40">CP cap</p>
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/85 font-medium tabular-nums">
            {parsed.cp_cap.toLocaleString()}
          </span>
        </div>
      )}

      {/* Eligible types — colored chips, one per type */}
      {parsed.eligible_types.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-1.5">
            Eligible types
          </p>
          <div className="flex flex-wrap gap-1.5">
            {parsed.eligible_types.map((t) => (
              <span
                key={t}
                className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${
                  TYPE_CHIP_CLASSES[t] ?? "bg-white/10 text-white/70 border-white/15"
                }`}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Banned Pokémon list */}
      {parsed.banned_pokemon.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-1.5">
            Banned ({parsed.banned_pokemon.length})
          </p>
          <p className="text-xs text-white/65 leading-relaxed">
            {parsed.banned_pokemon.join(", ")}
          </p>
        </div>
      )}

      {/* Full narrative — always shown as fallback / source-of-truth */}
      {cup.rules_summary && (
        <div className="mt-3 pt-3 border-t border-white/[0.06]">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-1.5">
            Full rules (from the official article)
          </p>
          <p className="text-xs text-white/55 leading-relaxed italic">{cup.rules_summary}</p>
        </div>
      )}

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}
